import { Image } from 'expo-image';
import Calendar from 'lucide-react-native/icons/calendar';
import CalendarCheck from 'lucide-react-native/icons/calendar-check';
import CalendarDays from 'lucide-react-native/icons/calendar-days';
import CalendarRange from 'lucide-react-native/icons/calendar-range';
import MapPin from 'lucide-react-native/icons/map-pin';
import RotateCcw from 'lucide-react-native/icons/rotate-ccw';
import { useMemo } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { Radius, Spacing } from '@/constants/theme';
import { useFilters } from '@/hooks/use-filters';
import { useTheme } from '@/hooks/use-theme';
import { APP_TZ, isSameDay, upcomingDays } from '@todo/core/date-range';
import { formatDayNumber, formatWeekday } from '@todo/core/i18n/format';
import type { Locale } from '@todo/core/i18n/locales';
import { useLocale } from '@todo/core/i18n/use-locale';
import { cities, dateFilters } from '@/lib/events-data';

import css from './filters-bar.module.css';

const dateIcons = [CalendarRange, CalendarCheck, CalendarDays, Calendar, Calendar];

// Scroll target for the hero's "Pick a date" option — see hero-search.web.tsx.
export const CALENDAR_ANCHOR_ID = 'filters-calendar';

type ThemeShape = ReturnType<typeof useTheme>;

type FiltersBarProps = {
  /** 'desktop' skips the mobile chip-scroller markup (see FiltersBar). */
  variant?: 'responsive' | 'desktop';
};

function DayPicker({
  days,
  selected,
  onSelect,
  theme,
  locale,
}: {
  days: Date[];
  selected: Date | null;
  onSelect: (date: Date) => void;
  theme: ThemeShape;
  locale: Locale;
}) {
  return (
    <View style={styles.dayGrid}>
      {days.map((day) => {
        const active = selected != null && isSameDay(selected, day);
        return (
          <Pressable
            key={day.toISOString()}
            accessibilityRole="button"
            accessibilityLabel={`${formatWeekday(day, APP_TZ, locale)} ${formatDayNumber(day, APP_TZ, locale)}`}
            onPress={() => onSelect(day)}
            style={({ pressed }) => [
              styles.dayChip,
              {
                backgroundColor: active ? theme.accent : theme.card,
                borderColor: active ? theme.accent : theme.border,
              },
              pressed && { opacity: 0.85 },
            ]}>
            <Text
              style={[
                styles.dayWeekday,
                { color: active ? theme.accentForeground : theme.mutedForeground },
              ]}>
              {formatWeekday(day, APP_TZ, locale)}
            </Text>
            <Text
              style={[
                styles.dayNum,
                { color: active ? theme.accentForeground : theme.text },
              ]}>
              {formatDayNumber(day, APP_TZ, locale)}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

// Web-only: renders both the mobile (horizontal chip scroller) and desktop
// (vertical list) markups and lets a CSS media query (filters-bar.module.css)
// pick which one is visible, instead of a JS `isLg` check that only resolves
// after mount (see the module comment for why, and the native filters-bar.tsx
// for the JS-branched version used there).
//
// `variant="desktop"` renders only the vertical list: for the detail pages,
// whose filters sidebar is hidden below the desktop breakpoint, so the chip
// scroller would be ~15KB of static HTML that's never visible.
export function FiltersBar({ variant = 'responsive' }: FiltersBarProps = {}) {
  const theme = useTheme();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { activeCity, setActiveCity, activeDate, setActiveDate, customDate, setCustomDate, reset } =
    useFilters();

  const days = useMemo(() => upcomingDays(14), []);

  const onSelectDate = (id: string) => {
    setActiveDate(id);
    if (id === 'custom' && !customDate) {
      setCustomDate(days[0]);
    }
  };

  return (
    <div id={CALENDAR_ANCHOR_ID}>
      <div className={css.desktopOnly}>
        <View style={styles.desktopWrap}>
          <Text style={[styles.heading, { color: theme.text }]}>{t('filters.chooseCity')}</Text>
          <View style={styles.desktopList}>
            <Pressable
              onPress={() => setActiveCity('all')}
              style={({ pressed }) => [
                styles.desktopRow,
                {
                  backgroundColor: activeCity === 'all' ? theme.accent : 'transparent',
                },
                pressed && { opacity: 0.85 },
              ]}>
              <View
                style={[
                  styles.desktopDot,
                  {
                    backgroundColor:
                      activeCity === 'all' ? theme.gold : theme.secondary,
                  },
                ]}>
                <MapPin
                  size={13}
                  color={
                    activeCity === 'all' ? theme.goldForeground : theme.mutedForeground
                  }
                />
              </View>
              <Text
                style={[
                  styles.desktopLabel,
                  {
                    color:
                      activeCity === 'all' ? theme.accentForeground : theme.text,
                    fontWeight: activeCity === 'all' ? '600' : '500',
                  },
                ]}>
                {t('common.wholeRegion')}
              </Text>
            </Pressable>
            {cities.map((city) => {
              const active = activeCity === city.id;
              return (
                <Pressable
                  key={city.id}
                  onPress={() => setActiveCity(city.id)}
                  style={({ pressed }) => [
                    styles.desktopRow,
                    { backgroundColor: active ? theme.accent : 'transparent' },
                    pressed && { opacity: 0.85 },
                  ]}>
                  <Image source={city.image} style={styles.desktopCityImg} contentFit="cover" />
                  <Text
                    style={[
                      styles.desktopLabel,
                      {
                        color: active ? theme.accentForeground : theme.text,
                        fontWeight: active ? '600' : '500',
                      },
                    ]}>
                    {city.name}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          <Text style={[styles.heading, { color: theme.text, marginTop: Spacing.three }]}>
            {t('filters.filterByDate')}
          </Text>
          <View style={styles.desktopList}>
            {dateFilters.map((filter, i) => {
              const Icon = dateIcons[i] ?? Calendar;
              const active = activeDate === filter.id;
              return (
                <Pressable
                  key={filter.id}
                  onPress={() => onSelectDate(filter.id)}
                  style={({ pressed }) => [
                    styles.desktopRow,
                    { backgroundColor: active ? theme.accent : 'transparent' },
                    pressed && { opacity: 0.85 },
                  ]}>
                  <Icon
                    size={16}
                    color={active ? theme.gold : theme.mutedForeground}
                    style={{ marginHorizontal: 4 }}
                  />
                  <Text
                    style={[
                      styles.desktopLabel,
                      {
                        color: active ? theme.accentForeground : theme.text,
                        fontWeight: active ? '600' : '500',
                      },
                    ]}>
                    {t(`dateFilters.${filter.id}`)}
                  </Text>
                </Pressable>
              );
            })}
          </View>

          {activeDate === 'custom' && (
            <DayPicker
              days={days}
              selected={customDate}
              onSelect={setCustomDate}
              theme={theme}
              locale={locale}
            />
          )}

          <View style={styles.desktopActions}>
            <Button variant="default" fullWidth>
              {t('filters.showAllEvents')}
            </Button>
            <Pressable
              onPress={reset}
              style={({ pressed }) => [styles.reset, pressed && { opacity: 0.6 }]}>
              <RotateCcw size={14} color={theme.mutedForeground} />
              <Text style={[styles.resetText, { color: theme.mutedForeground }]}>
                {t('filters.resetFilters')}
              </Text>
            </Pressable>
          </View>
        </View>
      </div>

      {variant === 'responsive' ? (
        <div className={css.mobileOnly}>
          <View style={styles.wrapper}>
            <Text style={[styles.heading, { color: theme.text }]}>{t('filters.chooseCity')}</Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.chipsRow}>
              <Pressable
                onPress={() => setActiveCity('all')}
                style={({ pressed }) => [
                  styles.chip,
                  {
                    backgroundColor: activeCity === 'all' ? theme.accent : theme.card,
                    borderColor: activeCity === 'all' ? theme.accent : theme.border,
                  },
                  pressed && { opacity: 0.85 },
                ]}>
                <View
                  style={[
                    styles.chipDot,
                    {
                      backgroundColor:
                        activeCity === 'all' ? theme.gold : theme.secondary,
                    },
                  ]}>
                  <MapPin
                    size={12}
                    color={activeCity === 'all' ? theme.goldForeground : theme.mutedForeground}
                  />
                </View>
                <Text
                  style={[
                    styles.chipText,
                    {
                      color:
                        activeCity === 'all' ? theme.accentForeground : theme.text,
                    },
                  ]}>
                  {t('common.wholeRegion')}
                </Text>
              </Pressable>
              {cities.map((city) => {
                const active = activeCity === city.id;
                return (
                  <Pressable
                    key={city.id}
                    onPress={() => setActiveCity(city.id)}
                    style={({ pressed }) => [
                      styles.chip,
                      {
                        backgroundColor: active ? theme.accent : theme.card,
                        borderColor: active ? theme.accent : theme.border,
                      },
                      pressed && { opacity: 0.85 },
                    ]}>
                    <Image source={city.image} style={styles.cityImg} contentFit="cover" />
                    <Text
                      style={[
                        styles.chipText,
                        { color: active ? theme.accentForeground : theme.text },
                      ]}>
                      {city.name}
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>

            <Text style={[styles.heading, { color: theme.text, marginTop: Spacing.two }]}>
              {t('filters.filterByDate')}
            </Text>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerStyle={styles.chipsRow}>
              {dateFilters.map((filter, i) => {
                const Icon = dateIcons[i] ?? Calendar;
                const active = activeDate === filter.id;
                return (
                  <Pressable
                    key={filter.id}
                    onPress={() => onSelectDate(filter.id)}
                    style={({ pressed }) => [
                      styles.chip,
                      {
                        backgroundColor: active ? theme.accent : theme.card,
                        borderColor: active ? theme.accent : theme.border,
                      },
                      pressed && { opacity: 0.85 },
                    ]}>
                    <Icon size={14} color={active ? theme.gold : theme.mutedForeground} />
                    <Text
                      style={[
                        styles.chipText,
                        { color: active ? theme.accentForeground : theme.text },
                      ]}>
                      {t(`dateFilters.${filter.id}`)}
                    </Text>
                  </Pressable>
                );
              })}
            </ScrollView>

            {activeDate === 'custom' && (
              <DayPicker
                days={days}
                selected={customDate}
                onSelect={setCustomDate}
                theme={theme}
                locale={locale}
              />
            )}

            <View style={styles.actions}>
              <Button variant="default" fullWidth>
                {t('filters.showAllEvents')}
              </Button>
              <Pressable
                onPress={reset}
                style={({ pressed }) => [styles.reset, pressed && { opacity: 0.6 }]}>
                <RotateCcw size={14} color={theme.mutedForeground} />
                <Text style={[styles.resetText, { color: theme.mutedForeground }]}>
                  {t('filters.resetFilters')}
                </Text>
              </Pressable>
            </View>
          </View>
        </div>
      ) : null}
    </div>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    paddingHorizontal: Spacing.three,
    gap: Spacing.two,
  },
  heading: {
    fontSize: 13,
    fontWeight: '700',
    marginBottom: 4,
  },
  chipsRow: {
    gap: Spacing.one + 2,
    paddingRight: Spacing.three,
  },
  chip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingLeft: 6,
    paddingRight: Spacing.two,
    paddingVertical: 6,
    borderWidth: 1,
    borderRadius: 999,
  },
  chipDot: {
    width: 22,
    height: 22,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cityImg: {
    width: 22,
    height: 22,
    borderRadius: 999,
  },
  chipText: {
    fontSize: 13,
    fontWeight: '500',
  },
  dayGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: Spacing.one,
    marginTop: Spacing.one,
  },
  dayChip: {
    width: 46,
    paddingVertical: 6,
    borderWidth: 1,
    borderRadius: Radius.md,
    alignItems: 'center',
    gap: 2,
  },
  dayWeekday: {
    fontSize: 11,
    fontWeight: '500',
  },
  dayNum: {
    fontSize: 15,
    fontWeight: '700',
  },
  actions: {
    marginTop: Spacing.two,
    gap: Spacing.two,
    alignItems: 'center',
  },
  reset: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: 6,
  },
  resetText: {
    fontSize: 13,
  },
  desktopWrap: {
    gap: 4,
  },
  desktopList: {
    gap: 2,
  },
  desktopRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.two,
    paddingHorizontal: Spacing.two,
    paddingVertical: Spacing.two,
    borderRadius: Radius.md,
  },
  desktopDot: {
    width: 24,
    height: 24,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  desktopCityImg: {
    width: 24,
    height: 24,
    borderRadius: 999,
  },
  desktopLabel: {
    fontSize: 14,
  },
  desktopActions: {
    gap: Spacing.two,
    alignItems: 'center',
    marginTop: Spacing.three,
  },
});
